<template>
  <div class="calculator-container tw-p-16">
    <div class="calculator-header">
      <h2>Comparison Calculator</h2>
    </div>

    <div class="inputs-section">
      <div class="field-group">
        <label>Asking Price (£)</label>
        <input v-model.number="askingPrice" type="number" class="calculator-input" placeholder="Enter asking price" />
      </div>

      <div class="grid md:grid-cols-2 gap-4">
        <div class="field-group">
          <label>Agent A Achieved %</label>
          <input v-model.number="agentA.achieved" type="number" class="calculator-input" placeholder="e.g. 100" />
        </div>
        <div class="field-group">
          <label>Agent B Achieved %</label>
          <input v-model.number="agentB.achieved" type="number" class="calculator-input" placeholder="e.g. 95" />
        </div>
      </div>

      <div class="grid md:grid-cols-2 gap-4">
        <div class="field-group">
          <label>Agent A Fee %</label>
          <input v-model.number="agentA.fee" type="number" class="calculator-input" placeholder="e.g. 1.5" />
        </div>
        <div class="field-group">
          <label>Agent B Fee %</label>
          <input v-model.number="agentB.fee" type="number" class="calculator-input" placeholder="e.g. 1.0" />
        </div>
      </div>

      <div class="calculate-section">
        <button @click="calculate" :disabled="!validInputs" class="n-primary tw-w-full md:tw-w-auto">
          Calculate Comparison
        </button>
      </div>
    </div>

    <div v-if="showResults" class="results-section">
      <div class="results-table">
        <div class="table-row header-row">
          <div class="cell">Agent</div>
          <div class="cell text-right">Sale Price</div>
          <div class="cell text-right">Fee</div>
          <div class="cell text-right">Net to Seller</div>
        </div>

        <div class="table-row">
          <div class="cell">Agent A</div>
          <div class="cell text-right">
            £{{ agentA.salePrice.toLocaleString(undefined, { maximumFractionDigits: 2 }) }}
          </div>
          <div class="cell text-right">
            £{{ agentA.feePounds.toLocaleString(undefined, { maximumFractionDigits: 2 }) }}
          </div>
          <div class="cell text-right">£{{ agentA.net.toLocaleString(undefined, { maximumFractionDigits: 2 }) }}</div>
        </div>

        <div class="table-row">
          <div class="cell">Agent B</div>
          <div class="cell text-right">
            £{{ agentB.salePrice.toLocaleString(undefined, { maximumFractionDigits: 2 }) }}
          </div>
          <div class="cell text-right">
            £{{ agentB.feePounds.toLocaleString(undefined, { maximumFractionDigits: 2 }) }}
          </div>
          <div class="cell text-right">£{{ agentB.net.toLocaleString(undefined, { maximumFractionDigits: 2 }) }}</div>
        </div>

        <div class="table-row total-row">
          <div class="cell">Extra in Your Pocket</div>
          <div class="cell text-right" colspan="3">
            £{{ extra.toLocaleString(undefined, { maximumFractionDigits: 2 }) }}
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="!validInputs" class="error-message">Please enter all values to calculate.</div>
  </div>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';
export default defineNuxtComponent({
  name: 'ComparisonCalculator',

  mixins: [ComponentMixin],

  data() {
    return {
      askingPrice: null as number | null,
      agentA: {
        achieved: 100,
        fee: 1.5,
        salePrice: 0,
        feePounds: 0,
        net: 0,
      },
      agentB: {
        achieved: 95,
        fee: 1.0,
        salePrice: 0,
        feePounds: 0,
        net: 0,
      },
      showResults: false,
    };
  },

  computed: {
    validInputs(): boolean {
      return (
        this.askingPrice &&
        this.askingPrice > 0 &&
        this.agentA.achieved > 0 &&
        this.agentB.achieved > 0 &&
        this.agentA.fee > 0 &&
        this.agentB.fee > 0
      );
    },

    extra(): number {
      return this.agentA.net - this.agentB.net;
    },
  },

  methods: {
    calculate(): void {
      if (!this.validInputs) return;

      this.agentA.salePrice = (this.askingPrice! * this.agentA.achieved) / 100;
      this.agentB.salePrice = (this.askingPrice! * this.agentB.achieved) / 100;

      this.agentA.feePounds = (this.agentA.salePrice * this.agentA.fee) / 100;
      this.agentB.feePounds = (this.agentB.salePrice * this.agentB.fee) / 100;

      this.agentA.net = this.agentA.salePrice - this.agentA.feePounds;
      this.agentB.net = this.agentB.salePrice - this.agentB.feePounds;

      this.showResults = true;
    },
  },
});
</script>

<style scoped>
.calculator-container {
  padding: 1rem 0;
  margin: 0 auto;
  border-radius: var(--rounded);
}

.calculator-header {
  text-align: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
}

.calculator-header h2 {
  margin: 0;
  color: var(--c-secondary);
  font-size: 1.5rem;
  text-align: start;
}

.inputs-section {
  margin-bottom: 2rem;
}

.field-group {
  margin-bottom: 1.5rem;
}

.field-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--c-secondary);
}

.calculator-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: var(--rounded);
  transition: all 0.2s ease;
}

.calculator-input:focus {
  outline: none;
  border-color: var(--c-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.calculate-section {
  margin-top: 2rem;
  text-align: start;
}

.results-section {
  margin-top: 2rem;
}

.results-table {
  border-radius: var(--rounded);
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.table-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.header-row {
  background: var(--c-primary);
  color: white;
}

.total-row {
  color: var(--c-primary);
  border-top: 2px solid var(--c-primary);
  border-bottom: 2px solid var(--c-primary);
}

.cell {
  flex: 1;
}

.text-right {
  text-align: right;
}

.error-message {
  padding: 1.5rem;
  color: #dc2626;
  border-radius: var(--rounded);
  text-align: center;
  margin-top: 1rem;
}
</style>
